import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppStore } from '@store/appStore'
import { usePWAUpdate } from '@hooks/usePWAUpdate'
import { MorphViewerPage } from '@components/GestureViewer/MorphViewerPage'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import { DatasetsManifest } from './types/dataset'
import { apiUrl, initBlobDirectAccess } from './config'

interface AppProps {
  initialShopId?: string | null
}

function App({ initialShopId }: AppProps) {
  usePWAUpdate() // Silent auto-update: SW controllerchange + version.json polling
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const currentShopId = useAppStore((state) => state.currentShopId)
  const availableDatasets = useAppStore((state) => state.availableDatasets)
  const setAvailableShops = useAppStore((state) => state.setAvailableShops)
  const setAvailableDatasets = useAppStore((state) => state.setAvailableDatasets)
  const setIsLoadingDatasets = useAppStore((state) => state.setIsLoadingDatasets)
  
  // Track whether datasets fetch has completed (not just started)
  const [datasetsReady, setDatasetsReady] = useState(false)
  
  // Which shop to show.
  //
  // The URL says which shop was asked for. `currentShopId` says which *dataset* is
  // loaded for it — the resolved id (shop_x → shop_x_SMART_SECTIONS_100), which the
  // viewer needs to find the scan, so it is preferred once available. But it outlives
  // a client-side navigation, so it is only trusted while it still belongs to the shop
  // in the URL: trusting it unconditionally is what made "shop A → back → shop B"
  // re-open shop A, since the store was still holding A.
  const shopIdFromUrl = searchParams.get('shopid') ?? initialShopId ?? null
  const loadedShopId = useMemo(() => {
    if (!currentShopId) return null
    if (!shopIdFromUrl || currentShopId === shopIdFromUrl) return currentShopId
    const loaded = availableDatasets.find((d) => d.id === currentShopId)
    return loaded?.shopId === shopIdFromUrl ? currentShopId : null
  }, [currentShopId, shopIdFromUrl, availableDatasets])
  const effectiveShopId = loadedShopId ?? shopIdFromUrl

  // Load datasets manifest on app mount
  useEffect(() => {
    const loadDatasets = async () => {
      setIsLoadingDatasets(true)
      try {
        console.log('📦 [App] Loading datasets manifest...')
        
        // Load from API (Cosmos DB)
        // Include auth headers so private shop datasets are returned
        const authToken = localStorage.getItem('dukkan_token')
        const headers: Record<string, string> = {}
        if (authToken) headers['Authorization'] = `Bearer ${authToken}`
        let response = await fetch(apiUrl('/api/datasets'), { headers })
        let source = 'API (Cosmos DB)'
        
        if (!response.ok) throw new Error('Failed to load datasets manifest')
        
        const manifest: DatasetsManifest = await response.json()
        console.log(`📦 [App] Manifest loaded from ${source}:`, {
          total: manifest.total_datasets,
          datasets: manifest.datasets.map(d => ({ id: d.id, name: d.name, cameras: d.camera_count }))
        })
        
        // Initialize direct blob access BEFORE setting datasets
        // This ensures resolveUrl() uses direct blob URLs when datasets paths are resolved
        await initBlobDirectAccess()
        
        setAvailableDatasets(manifest.datasets)
        
        // Populate available shops from datasets
        const shopIds = manifest.datasets.map(dataset => dataset.id)
        console.log('📦 [App] Available shops:', shopIds)
        setAvailableShops(shopIds)
      } catch (error) {
        console.error('❌ [App] Failed to load datasets:', error)
      } finally {
        setIsLoadingDatasets(false)
        setDatasetsReady(true)
      }
    }
    
    loadDatasets()
  }, [setAvailableDatasets, setAvailableShops, setIsLoadingDatasets])

  // Load initial shop from URL parameter (only after datasets are loaded)
  // Note: GestureViewer will handle the actual loading, this just ensures datasets are ready
  useEffect(() => {
    if (initialShopId && availableDatasets.length > 0) {
      console.log(`🔗 [App] Shop ID detected in URL: ${initialShopId}`)
      console.log(`🔗 [App] Available datasets:`, availableDatasets.map(d => d.id))
      // Don't call loadDataset here - GestureViewer will handle it when it mounts
    }
  }, [initialShopId, availableDatasets])

  const directMorphUrl = searchParams.get('morphUrl')

  return (
    <ErrorBoundary>
      <div className="app">
        {directMorphUrl ? (
          <MorphViewerPage shopId="__direct__" directMorphUrl={directMorphUrl} />
        ) : effectiveShopId ? (
          // Shop ID present — render viewer once manifest fetch is done (mobile shops may not be in manifest)
          datasetsReady ? (
            // key: switching shops without a page reload must start the viewer clean —
            // its per-mount refs (morph URL, visit-tracked, mobile-shop-loaded) otherwise
            // stay pinned to the previous shop. Keyed on the URL's id, not the resolved
            // one, so resolution mid-load stays a prop change instead of a remount.
            <MorphViewerPage key={shopIdFromUrl ?? effectiveShopId} shopId={effectiveShopId} />
          ) : (
            // Still loading datasets manifest — show loading state
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f172a', color: '#94a3b8', fontSize: 16 }}>
              Loading shop data...
            </div>
          )
        ) : (
          // No shop ID in URL — redirect to browse
          <RedirectToBrowse navigate={navigate} />
        )}
      </div>
    </ErrorBoundary>
  )
}

/** Redirect to /browse when no shop is selected (replaces old ShopSelector fallback) */
function RedirectToBrowse({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  useEffect(() => {
    navigate('/browse', { replace: true })
  }, [navigate])
  return null
}

export default App
